// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useEffect } from 'react';
import { providerSubmissionApi } from '../api/providerSubmissionApi';
import type { ProviderSubmissionDTO } from '../types/providerAccess';

interface UseProviderSubmissionsReturn {
  data: ProviderSubmissionDTO[] | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  reviewSubmission: (submissionId: string, reviewNotes?: string) => Promise<boolean>;
  reviewing: boolean;
  downloadDocument: (submissionId: string, filename: string) => Promise<void>;
  downloading: boolean;
}

export function useProviderSubmissions(policyId: string): UseProviderSubmissionsReturn {
  const [data, setData] = useState<ProviderSubmissionDTO[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const refresh = useCallback(async () => {
    if (!policyId) return;
    try {
      setLoading(true);
      setError(null);
      const submissions = await providerSubmissionApi.listSubmissions(policyId);
      setData(submissions);
    } catch (err) {
      console.error('Failed to load provider submissions:', err);
      setError('Failed to load provider submissions');
    } finally {
      setLoading(false);
    }
  }, [policyId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const reviewSubmission = useCallback(
    async (submissionId: string, reviewNotes?: string): Promise<boolean> => {
      try {
        setReviewing(true);
        await providerSubmissionApi.reviewSubmission(policyId, submissionId, reviewNotes ? { reviewNotes } : undefined);
        await refresh();
        return true;
      } catch (err) {
        console.error('Failed to review submission:', err);
        setError('Failed to review submission');
        return false;
      } finally {
        setReviewing(false);
      }
    },
    [policyId, refresh],
  );

  const downloadDocument = useCallback(
    async (submissionId: string, filename: string): Promise<void> => {
      try {
        setDownloading(true);
        const blob = await providerSubmissionApi.downloadDocument(policyId, submissionId);
        const url = globalThis.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        globalThis.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Failed to download document:', err);
        setError('Failed to download document');
      } finally {
        setDownloading(false);
      }
    },
    [policyId],
  );

  return { data, loading, error, refresh, reviewSubmission, reviewing, downloadDocument, downloading };
}
